
import React, { useState } from 'react';
import { Product } from '@/lib/supabase/products/types';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useOrderFormState } from './hooks/useOrderFormState';
import { useOrderFormSubmit } from './hooks/useOrderFormSubmit';
import { useShopTheme } from './hooks/useShopTheme';
import SimpleOrderForm from './SimpleOrderForm';
import DynamicOrderForm from './DynamicOrderForm';
import ProductPriceDisplay from './components/ProductPriceDisplay';
import ProductOrderSummary from './components/ProductOrderSummary';

interface ProductOrderFormProps {
  product: Product;
  shopId: string | null;
  organizationId: string | null;
}

const ProductOrderForm = ({ product, shopId, organizationId }: ProductOrderFormProps) => {
  const [quantity, setQuantity] = useState(1);
  
  // Get shop theme (color and button text)
  const { shopColor, ctaButtonText } = useShopTheme(shopId);
  
  // Get form state (all form fields)
  const { formData, setters, resetForm } = useOrderFormState();
  
  // Get form submission handler
  const { submitting, handleSubmit } = useOrderFormSubmit(product, organizationId, shopId);
  
  const inStock = product.is_stock_managed ? product.stock_quantity > 0 : true;
  
  const actualPrice = product.discount_price || product.price;
  const totalPrice = actualPrice * quantity;
  
  const incrementQuantity = () => {
    if (product.is_stock_managed && quantity >= product.stock_quantity) return;
    setQuantity(prev => prev + 1);
    setters.setQuantity(quantity + 1);
  };
  
  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
      setters.setQuantity(quantity - 1);
    }
  };

  console.log("ProductOrderForm rendering with form data:", formData);

  return (
    <div className="sticky top-4">
      <Card className="border border-gray-200 dark:border-gray-700 shadow-sm">
        <CardContent className="pt-6">
          <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white">{product.name}</h1>
          
          {/* Price Display */}
          <ProductPriceDisplay 
            price={product.price} 
            discountPrice={product.discount_price} 
            currency={product.currency} 
          />
          
          <Separator className="my-6" />
          
          {/* Order Form */}
          <form onSubmit={(e) => handleSubmit(e, formData, resetForm)}>
            {/* Dynamically display form fields based on form_id */}
            {product.form_id ? (
              <DynamicOrderForm
                formId={product.form_id}
                quantity={formData.quantity}
                setQuantity={setters.setQuantity}
                customerName={formData.customerName}
                setCustomerName={setters.setCustomerName}
                customerPhone={formData.customerPhone}
                setCustomerPhone={setters.setCustomerPhone}
                customerEmail={formData.customerEmail}
                setCustomerEmail={setters.setCustomerEmail}
                customerAddress={formData.customerAddress}
                setCustomerAddress={setters.setCustomerAddress}
                customerRegion={formData.customerRegion}
                setCustomerRegion={setters.setCustomerRegion}
                customerMunicipality={formData.customerMunicipality}
                setCustomerMunicipality={setters.setCustomerMunicipality}
                note={formData.note}
                setNote={setters.setNote}
              />
            ) : (
              <SimpleOrderForm
                formData={formData}
                setters={setters}
              />
            )}
            
            {/* Product Summary */}
            <ProductOrderSummary 
              product={product}
              quantity={quantity}
              totalPrice={totalPrice}
              incrementQuantity={incrementQuantity}
              decrementQuantity={decrementQuantity}
              submitting={submitting}
              inStock={inStock}
              shopColor={shopColor}
              ctaButtonText={ctaButtonText}
              formId={product.form_id}
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductOrderForm;
